import { Ingredient } from '../types';
import { getBaseMeasurementIndex } from './unitConversion';

/** One ingredient of the `addMeal` / `editMeal` mutation input. */
export interface RequestIngredient {
  productId?: string;
  name: string;
  brand?: string;
  baseAmount: number;
  baseUnit: string;
  calories: number;
  proteinGrams: number | undefined;
  fatGrams: number | undefined;
  carbsGrams: number | undefined;
  fiberGrams?: number;
  measureId?: number;
  measureAmount?: number;
  isUserProduct: boolean;
}

const API_UNITS: { [unit: string]: string } = {
  g: 'grams',
  ml: 'milliliters',
  'fl oz': 'fl_oz',
};

const toApiUnit = (unit: string): string => API_UNITS[unit] ?? unit;

/**
 * Maps a form ingredient to the shape both meal editors (tracker and "Your meals") send on save.
 *
 * Kept in one place so an ingredient-level field reaches the backend from every editor — the two
 * editors used to carry their own copies and fields kept being added to only one of them.
 */
export const toRequestIngredient = (ingredient: Ingredient): RequestIngredient => {
  const baseIndex = getBaseMeasurementIndex(ingredient.measurements);
  const baseMeasurement = ingredient.measurements[baseIndex];
  const householdMeasurement = ingredient.measurements.find(
    (_measurement, index) => index !== baseIndex
  );

  const request: RequestIngredient = {
    // Links the logged copy back to the catalogue product; a hand-typed ingredient has no
    // product, so the key is left out rather than sent empty.
    ...(ingredient.productId && { productId: ingredient.productId }),
    name: ingredient.name,
    brand: ingredient.brand,
    baseAmount: baseMeasurement.amount,
    baseUnit: toApiUnit(baseMeasurement.unit),
    calories: Math.round(ingredient.kcal),
    proteinGrams: ingredient.macro?.protein.grams,
    fatGrams: ingredient.macro?.fat.grams,
    carbsGrams: ingredient.macro?.carbohydrates.grams,
    // editMeal resets an ingredient's fiber to 0 when it is omitted, so it is always sent back
    fiberGrams: ingredient.macro?.fiber?.grams,
    // Only the client knows this: a saved meal keeps copies of the values, so without sending it
    // the backend cannot tell a user product from a public one.
    isUserProduct: Boolean(ingredient.isUserProduct),
  };

  if (householdMeasurement) {
    request.measureId = householdMeasurement.measureId;
    request.measureAmount = householdMeasurement.amount;
  }

  return request;
};
